import { type JSX, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigation } from 'react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { isSamePathnameListingDataNavigationPending } from '@/lib/plp-panel-url-state';
import { getPlpPaginationItems } from '@/lib/pagination-utils';
import { cn } from '@/lib/utils';
import { PaginationChevronButton, PaginationEllipsis, PaginationPageButton } from './pagination-plp-controls';
import { PRODUCT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';

export default function CustomPagination({
    limit,
    currentPage,
    total,
    setPageNumber,
}: {
    limit: number;
    /** 1-based page from the loader (authoritative once navigation completes). */
    currentPage: number;
    total: number;
    setPageNumber?: (page: number) => void;
}): JSX.Element | null {
    const location = useLocation();
    const navigation = useNavigation();
    const { t } = useTranslation('category');
    const isPending = useMemo(
        () =>
            isSamePathnameListingDataNavigationPending({
                pathname: location.pathname,
                search: location.search,
                navigationState: navigation.state,
                navigationLocation: navigation.location,
            }),
        [location.pathname, location.search, navigation.location, navigation.state]
    );

    /**
     * While a navigation is pending, `navigation.location` holds the target URL so we can read the intended
     * `page` param immediately. Otherwise use the server-provided current page.
     */
    const effectivePage = useMemo(() => {
        if (navigation.state === 'idle' || !navigation.location) {
            return currentPage;
        }
        const params = new URLSearchParams(navigation.location.search);
        const pageParam = params.get(PRODUCT_SEARCH_QUERY_PARAMS.PAGE);

        return pageParam ? Number(pageParam) : 1;
    }, [navigation.state, navigation.location, currentPage]);

    const totalPages = Math.ceil(total / limit);
    const pageNumbers = useMemo(() => getPlpPaginationItems(totalPages, effectivePage), [totalPages, effectivePage]);

    useEffect(() => {
        if (setPageNumber && totalPages > 0) {
            setPageNumber(effectivePage);
        }
    }, [effectivePage, setPageNumber, totalPages]);

    const getPageUrl = useCallback(
        (page: number): string => {
            const params = new URLSearchParams(location.search);

            if (page > 1) {
                params.set(PRODUCT_SEARCH_QUERY_PARAMS.PAGE, String(page));
            } else {
                params.delete(PRODUCT_SEARCH_QUERY_PARAMS.PAGE);
            }

            return params.toString() ? `?${params.toString()}` : '';
        },
        [location.search]
    );

    if (totalPages <= 1) {
        return null;
    }

    return (
        <div className="grid-global pt-8 md:pt-12">
            <nav
                className={cn(
                    'col-span-6 md:col-start-5 md:col-end-9 flex justify-center items-center gap-3',
                    isPending && 'pointer-events-none'
                )}
                aria-label={t('pagination.label')}>
                <PaginationChevronButton
                    to={getPageUrl(effectivePage - 1)}
                    disabled={effectivePage <= 1}
                    aria-label={t('pagination.previousPage')}>
                    <ChevronLeft aria-hidden className="size-6" />
                </PaginationChevronButton>

                {pageNumbers.map((item) =>
                    typeof item === 'object' ? (
                        <PaginationEllipsis key={`ellipsis-${item.key}`} />
                    ) : (
                        <PaginationPageButton
                            key={item}
                            to={getPageUrl(item)}
                            isActive={effectivePage === item}
                            disabled={effectivePage === item}
                            aria-label={t('pagination.page', { number: item })}
                            aria-current={effectivePage === item ? 'page' : undefined}>
                            {item}
                        </PaginationPageButton>
                    )
                )}

                <PaginationChevronButton
                    to={getPageUrl(effectivePage + 1)}
                    disabled={effectivePage === totalPages}
                    aria-label={t('pagination.nextPage')}>
                    <ChevronRight aria-hidden className="size-6" />
                </PaginationChevronButton>
            </nav>
        </div>
    );
}
